import { ASSIST_WINDOW } from "../constants.ts";
import type { Vec3 } from "../math.ts";
import type { Sim } from "./sim.ts";

type HeldEntry = { holder: number; onRelease?: (sim: Sim, target: number) => void };
const held = new WeakMap<Sim, Map<number, HeldEntry>>();

const entries = (sim: Sim) => {
  let m = held.get(sim);
  if (!m) {
    m = new Map();
    held.set(sim, m);
  }
  return m;
};

/** Server/prediction grab: target rides at offset until a shared release path clears it. */
export function grab(sim: Sim, holder: number, target: number, o: { offset: Vec3; seconds: number; onRelease?: (sim: Sim, target: number) => void }) {
  if (!sim.c.Player.alive[target] || sim.c.Status.invuln[target]! > 0 || sim.c.Status.unstoppable[target]! > 0) return;
  releaseHeldTarget(sim, target);
  const ticks = Math.round(o.seconds * 60);
  sim.c.Status.held[target] = Math.max(sim.c.Status.held[target]!, ticks);
  sim.c.Status.heldBy[target] = holder + 1;
  const m = sim.players.get(target);
  if (sim.authority && m && sim.c.Player.team[holder] !== sim.c.Player.team[target]) m.disabledBy.set(holder, sim.tick + Math.max(ticks, ASSIST_WINDOW));
  sim.c.Move.ticks[target] = 0;
  sim.c.Move.kind[target] = 0;
  sim.c.Move.x[target] = o.offset.x;
  sim.c.Move.y[target] = o.offset.y;
  sim.c.Move.z[target] = o.offset.z;
  entries(sim).set(target, { holder, onRelease: o.onRelease });
}

/** Explicitly releases every target currently held by `holder`. */
export function release(sim: Sim, holder: number) {
  // oxlint-disable-next-line unicorn/no-useless-spread
  for (const [target, e] of [...entries(sim)]) if (e.holder === holder) releaseHeldTarget(sim, target);
}

/** Releases one held target and fires its registered callback at most once. */
export function releaseHeldTarget(sim: Sim, target: number) {
  const m = entries(sim);
  const e = m.get(target);
  m.delete(target);
  sim.c.Status.held[target] = 0;
  sim.c.Status.heldBy[target] = 0;
  if (e) e.onRelease?.(sim, target);
}

/** Releases any target carried by a holder (death, stun, sleep, hero swap). */
export function releaseHeldBy(sim: Sim, holder: number) {
  release(sim, holder);
}

/** Carries held targets and releases on timer expiry, holder death/disable or target death. */
export function updateHeldPlayers(sim: Sim) {
  const c = sim.c;
  for (const e of sim.playerEids()) {
    if (!c.Player.alive[e] && c.Status.held[e]! > 0) {
      releaseHeldTarget(sim, e);
      continue;
    }
    if (c.Status.held[e]! <= 0) {
      if (c.Status.heldBy[e]! > 0) releaseHeldTarget(sim, e);
      continue;
    }
    const holder = c.Status.heldBy[e]! - 1;
    if (!sim.players.has(holder) || !c.Player.alive[holder] || c.Status.stun[holder]! > 0 || c.Status.sleep[holder]! > 0 || c.Status.knockdown[holder]! > 0 || c.Status.frozen[holder]! > 0) {
      releaseHeldTarget(sim, e);
      continue;
    }
    c.Pos.x[e] = c.Pos.x[holder]! + c.Move.x[e]!;
    c.Pos.y[e] = c.Pos.y[holder]! + c.Move.y[e]!;
    c.Pos.z[e] = c.Pos.z[holder]! + c.Move.z[e]!;
    c.Vel.x[e] = c.Vel.x[holder]!;
    c.Vel.y[e] = c.Vel.y[holder]!;
    c.Vel.z[e] = c.Vel.z[holder]!;
    c.Player.grounded[e] = c.Player.grounded[holder]!;
  }
}
